import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, Input, Select, TextArea, useToastActions } from '@airclou/ui';
import { createInvitation } from '@/api/groups';

interface InviteMemberModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteMemberModal({ companyId, isOpen, onClose, onSuccess }: InviteMemberModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error: showError } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [message, setMessage] = useState('');

  const handleSubmit = async () => {
    if (!email.trim()) return;

    setLoading(true);
    try {
      await createInvitation(companyId, {
        email: email.trim(),
        role,
        message: message.trim() || undefined,
      });
      success(t('people.invitationSent'));
      onSuccess();
    } catch (err: any) {
      const code = err.code;
      if (code === 'USER_ALREADY_EXISTS') {
        showError(t('people.userAlreadyExists'));
      } else if (code === 'INVITATION_ALREADY_PENDING') {
        showError(t('people.invitationAlreadyPending'));
      } else if (code === 'INVALID_EMAIL') {
        showError(t('people.invalidEmail'));
      } else {
        showError(err.message || t('people.failedToSend'));
      }
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = isValidEmail && !loading;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('people.inviteMemberTitle')}
      size="md"
      submitText={loading ? t('common.sending') : t('people.sendInvitation')}
      canSubmit={canSubmit}
      isLoading={loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.email')} <span className="text-red-500">*</span>
          </label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(value) => setEmail(value)}
            placeholder="newuser@example.com"
          />
        </div>

        <div>
          <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.role')}
          </label>
          <Select
            id="invite-role"
            value={role}
            onChange={(value) => setRole(value as 'admin' | 'member')}
            className="w-full"
            options={[
              { value: 'member', label: t('common.member') },
              { value: 'admin', label: t('common.admin') }
            ]}
          />
        </div>

        <div>
          <label htmlFor="invite-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('people.personalMessage')} <span className="text-gray-400">{t('people.optional')}</span>
          </label>
          <TextArea
            id="invite-message"
            value={message}
            onChange={setMessage}
            placeholder={t('people.personalMessagePlaceholder')}
            minRows={3}
            maxRows={6}
            resize="none"
          />
        </div>
      </div>
    </FormModal>
  );
}
